import React, { useState } from 'react';

import { FieldArray, FormikState } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, Segment } from 'semantic';

import { TariffRule as TariffRuleType } from 'subdomains/cpo/types/charging-policy';
import { FormController } from '../../../../../../components/FormController';
import { TariffRuleFormFields } from './TariffRuleFormFields';
import { TariffRulePreview } from './TariffRulePreview';
import { tariffRuleSchema } from '../../validation';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  createArrayFormItemWithId,
  useEnsureArrayFormItemIds,
} from 'utils/arrayformid';

type Props = {
  values?: FormikState<TariffRuleType[]>['values'];
  onEditFinished?: () => void;
  onStartEditing?: () => void;
};

export const TariffRules = (props: Props) => {
  const { t } = useTranslation();

  const tariffRulesWithTempIDs = useEnsureArrayFormItemIds(props.values);
  const [numOfEditingTariffRules, setNumOfEditingTariffRules] = useState(0);

  return (
    <>
      <FieldArray name="tariffRules">
        {({ remove, replace, move, unshift }) => {
          const onDragEnd = (result: DropResult) => {
            if (!result.destination) return;
            if (result.source.index !== result.destination.index) {
              move(result.source.index, result.destination.index);
            }
          };

          const onSubmit = (index: number, values: TariffRuleType) => {
            replace(index, {
              ...values,
              __initial: undefined,
            });

            const newNumOfEditingTariffRules = numOfEditingTariffRules - 1;
            setNumOfEditingTariffRules(Math.max(0, newNumOfEditingTariffRules));
            if (newNumOfEditingTariffRules === 0) {
              props.onEditFinished?.();
            }
          };

          const onEdit = () => {
            const newNumOfEditingTariffRules = numOfEditingTariffRules + 1;
            setNumOfEditingTariffRules(newNumOfEditingTariffRules);
            if (newNumOfEditingTariffRules === 1) {
              props.onStartEditing?.();
            }
          };

          const onNewTariffRule = () => {
            unshift(
              createArrayFormItemWithId({
                __initial: true,
                customerGroups: [],
                paymentOptions: [],
              })
            );

            const newNumOfEditingTariffRules = numOfEditingTariffRules + 1;
            setNumOfEditingTariffRules(newNumOfEditingTariffRules);
            if (newNumOfEditingTariffRules === 1) {
              props.onStartEditing?.();
            }
          };

          const onCancel = () => {
            const newNumOfEditingTariffRules = numOfEditingTariffRules - 1;
            setNumOfEditingTariffRules(Math.max(0, newNumOfEditingTariffRules));
            if (newNumOfEditingTariffRules === 0) {
              props.onEditFinished?.();
            }
          };

          return (
            <>
              <div
                style={{
                  display: 'flex',
                  marginBottom: '1rem',
                  width: '100%',
                }}>
                <Button
                  as="button"
                  basic
                  icon="plus"
                  style={{
                    border: '1px solid rgb(0, 0, 0, 0.5)',
                    width: '100%',
                    height: 50,
                  }}
                  content={t(
                    'providerAdmin.chargingPolicyForm.addTariffRuleButtonText',
                    'New tariff'
                  )}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    onNewTariffRule();
                  }}
                />
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="tariff-rules-droppable">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {tariffRulesWithTempIDs?.map((tariffRule, index) => (
                        <Draggable
                          key={tariffRule.__id}
                          draggableId={String(tariffRule.__id)}
                          index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                marginBottom: '1rem',
                                ...provided.draggableProps.style,
                              }}>
                              <FormController
                                values={tariffRule}
                                onSubmit={(values) => {
                                  onSubmit(index, values);
                                }}
                                onRemove={() => remove(index)}
                                validationSchema={tariffRuleSchema}
                                formComponent={TariffRuleFormFields}
                                previewComponent={TariffRulePreview}
                                openInEditMode={tariffRule.__initial}
                                removeOnCancel={tariffRule.__initial}
                                dragHandleProps={provided.dragHandleProps ?? {}}
                                currency="EUR"
                                onEdit={() => onEdit()}
                                onCancel={() => onCancel()}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {!tariffRulesWithTempIDs?.length && (
                        <Segment secondary style={{ textAlign: 'center' }}>
                          {t(
                            'providerAdmin.chargingPolicyForm.noTariffRules',
                            'There are no tariff rules to display.'
                          )}
                        </Segment>
                      )}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          );
        }}
      </FieldArray>
    </>
  );
};
